import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProgram,
  deleteProgram,
  listProgramPlaces,
  listPlaces,
  listHappenings,
  setProgramHappeningsPublic,
  listPlaceRoleDefaults,
  listPlaceRoleDayDefaults,
  listHappeningRoleRequirementsBatch,
  setHappeningRoleRequirements,
  type Happening,
  type Place,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { ProgramFormDialog } from '@/components/ProgramFormDialog';
import { ProgramStaffingView } from '@/components/staffing/ProgramStaffingView';
import { ChevronLeft, Eye, EyeOff, Pencil, Plus, Trash2, Users } from 'lucide-react';

const SLOT_MINUTES = 30;

// LocalStorage helpers for the program timetable view. We keep these tiny and
// inline rather than reaching for a Zustand store — the persistence is
// per-program and ephemeral (UX comfort, not user data).
const lsKey = {
  orientation: () => 'littlebooker.programTimetable.orientation',
  timeFormat: () => 'littlebooker.programTimetable.timeFormat',
  activeDay: (programId: string) => `littlebooker.programTimetable.${programId}.activeDay`,
  scroll: (programId: string, orientation: string) =>
    `littlebooker.programTimetable.${programId}.scroll.${orientation}`,
};

const safeRead = (key: string): string | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string): void => {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
  } catch {
    // ignore quota / privacy mode errors
  }
};

export default function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [materializeMsg, setMaterializeMsg] = useState<string | null>(null);

  const { data: program, isLoading: loadingProgram } = useQuery({
    queryKey: ['program', id],
    enabled: !!id,
    queryFn: () => (id ? getProgram(id) : Promise.resolve(null)),
  });

  const { data: stageLinks = [] } = useQuery({
    queryKey: ['program-places', id],
    enabled: !!id,
    queryFn: () => (id ? listProgramPlaces(id) : Promise.resolve([])),
  });

  const { data: allStages = [] } = useQuery({
    queryKey: ['places', { type: 'stage' }],
    queryFn: () => listPlaces({ type: 'stage' }),
  });

  // Hydrate the stages assigned to this program with full Place rows.
  const programStages = useMemo<Place[]>(() => {
    const byId = new Map(allStages.map((p) => [p.id, p]));
    return stageLinks
      .map((link) => byId.get(link.place_id))
      .filter((p): p is Place => !!p);
  }, [stageLinks, allStages]);

  const { data: happenings = [] } = useQuery({
    queryKey: ['happenings', { program_id: id }],
    enabled: !!id,
    queryFn: () => listHappenings({ program_id: id, includeArtists: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => (id ? deleteProgram(id) : Promise.reject(new Error('No id'))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      navigate('/programs');
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ isPublic }: { isPublic: boolean }) =>
      id
        ? setProgramHappeningsPublic(id, isPublic)
        : Promise.reject(new Error('No id')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['happenings', { program_id: id }] });
    },
  });

  // Apply each stage's default role requirements to its happenings, day-aware:
  // the effective default for a happening = its day's override (if any) on top of
  // the stage base. Adds missing roles and syncs counts to the effective value —
  // but never changes a role a happening explicitly set to 0 (deliberate "none",
  // e.g. a soundcheck), and leaves any extra non-default roles alone.
  const materializeMutation = useMutation({
    mutationFn: async (): Promise<number> => {
      if (!id) throw new Error('No id');
      const hs = await listHappenings({ program_id: id });
      const existing = await listHappeningRoleRequirementsBatch(hs.map((h) => h.id));
      const placeIds = Array.from(
        new Set(hs.map((h) => h.place_id).filter((p): p is string => !!p)),
      );
      const baseByPlace: Record<string, Array<{ role_id: string; count: number }>> = {};
      const dayByPlace: Record<string, Array<{ role_id: string; day: string; count: number }>> = {};
      await Promise.all(
        placeIds.map(async (pid) => {
          baseByPlace[pid] = (await listPlaceRoleDefaults(pid)).map((x) => ({ role_id: x.role_id, count: x.count }));
          dayByPlace[pid] = (await listPlaceRoleDayDefaults(pid)).map((x) => ({ role_id: x.role_id, day: x.day, count: x.count }));
        }),
      );
      const localDate = (iso: string) => {
        const d = new Date(iso);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };
      let updated = 0;
      for (const h of hs) {
        if (!h.place_id || !h.start_time) continue;
        // Effective defaults for this happening's day: base, then day overrides.
        const eff = new Map<string, number>();
        for (const b of baseByPlace[h.place_id] ?? []) eff.set(b.role_id, b.count);
        const date = localDate(h.start_time);
        for (const dd of dayByPlace[h.place_id] ?? []) if (dd.day === date) eff.set(dd.role_id, dd.count);
        if (eff.size === 0) continue;

        const reqMap = new Map((existing[h.id] ?? []).map((r) => [r.role_id, r.count]));
        let changed = false;
        for (const [role, count] of eff) {
          const cur = reqMap.get(role);
          if (cur === 0) continue; // preserve deliberate "none"
          if (cur === undefined || cur !== count) {
            reqMap.set(role, count);
            changed = true;
          }
        }
        if (!changed) continue;
        await setHappeningRoleRequirements(
          h.id,
          Array.from(reqMap, ([role_id, count]) => ({ role_id, count })),
        );
        updated += 1;
      }
      return updated;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['happening-role-requirements'] });
      setMaterializeMsg(
        updated === 0
          ? 'Happenings already match their stage defaults (nothing to change).'
          : `Applied stage defaults (incl. day overrides) to ${updated} happening${updated === 1 ? '' : 's'}. Run Auto-assign to regenerate shifts.`,
      );
    },
    onError: (err) => {
      setMaterializeMsg(`Failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    },
  });

  if (loadingProgram) {
    return <p className="p-6">Loading...</p>;
  }

  if (!program) {
    return (
      <div className="space-y-4 p-6">
        <p>Program not found.</p>
        <Link to="/programs" className="text-primary hover:underline">
          ← Back to programs
        </Link>
      </div>
    );
  }

  const startDate = new Date(program.start_time);
  const endDate = new Date(program.end_time);
  const returnTo = `/programs/${program.id}`;

  // Publication state across all non-tech happenings in this program.
  // 'all' = every performance is public, 'none' = none are, 'partial' = mixed.
  const publishablePerformances = happenings.filter((h) => h.type !== 'tech');
  const publicCount = publishablePerformances.filter((h) => h.is_public).length;
  const publishState: 'all' | 'none' | 'partial' =
    publishablePerformances.length === 0
      ? 'none'
      : publicCount === publishablePerformances.length
        ? 'all'
        : publicCount === 0
          ? 'none'
          : 'partial';

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/programs"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to programs
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{program.name}</h1>
          <p className="text-muted-foreground">
            {startDate.toLocaleString()} → {endDate.toLocaleString()}
            {' · '}
            {programStages.length} stage{programStages.length === 1 ? '' : 's'}
            {' · '}
            {(() => {
              const performanceCount = happenings.filter((h) => h.type !== 'tech').length;
              const techCount = happenings.length - performanceCount;
              return (
                <>
                  {performanceCount} performance{performanceCount === 1 ? '' : 's'}
                  {techCount > 0 && ` · ${techCount} tech`}
                </>
              );
            })()}
          </p>
          {program.description && (
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              {program.description}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {publishablePerformances.length > 0 && (
            <Button
              variant="outline"
              disabled={publishMutation.isPending}
              onClick={() => publishMutation.mutate({ isPublic: publishState !== 'all' })}
              title={
                publishState === 'all'
                  ? 'Hide all performances from the public website'
                  : `Publish ${publishablePerformances.length - publicCount} unpublished performance${
                      publishablePerformances.length - publicCount === 1 ? '' : 's'
                    } to the public website`
              }
            >
              {publishState === 'all' ? (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  Unpublish program
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  {publishState === 'partial'
                    ? `Publish all (${publicCount}/${publishablePerformances.length} public)`
                    : 'Publish program'}
                </>
              )}
            </Button>
          )}
          {programStages.length > 0 && (
            <Button
              variant="outline"
              disabled={materializeMutation.isPending}
              onClick={() => {
                setMaterializeMsg(null);
                materializeMutation.mutate();
              }}
              title="Fill staffing requirements from each stage's defaults for happenings that don't have any yet"
            >
              <Users className="mr-2 h-4 w-4" />
              {materializeMutation.isPending ? 'Applying…' : 'Apply staffing defaults'}
            </Button>
          )}
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button variant="outline" onClick={() => setDeleting(true)} className="text-destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {materializeMsg && (
        <div className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          {materializeMsg}
        </div>
      )}

      {programStages.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No stages assigned</CardTitle>
            <CardDescription>
              Add stages to this program before scheduling performances.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => setEditing(true)}>
              Manage stages
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="timetable">
          <TabsList>
            <TabsTrigger value="timetable">Timetable</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
            <TabsTrigger value="staffing">Staffing</TabsTrigger>
          </TabsList>

          <TabsContent value="timetable" className="mt-4">
            <TimetableView
              programId={program.id}
              stages={programStages}
              happenings={happenings}
              programStart={startDate}
              programEnd={endDate}
              returnTo={returnTo}
            />
          </TabsContent>

          <TabsContent value="list" className="mt-4">
            <ListView
              programId={program.id}
              stages={programStages}
              happenings={happenings}
              programStart={startDate}
              returnTo={returnTo}
            />
          </TabsContent>

          <TabsContent value="staffing" className="mt-4">
            <ProgramStaffingView
              program={program}
              stages={programStages}
              happenings={happenings}
              programStart={startDate}
              programEnd={endDate}
            />
          </TabsContent>
        </Tabs>
      )}

      {editing && (
        <ProgramFormDialog
          mode="edit"
          program={program}
          stages={allStages}
          onClose={() => setEditing(false)}
          onDeleteRequest={() => {
            setEditing(false);
            setDeleting(true);
          }}
        />
      )}

      {deleting && (
        <DeleteConfirmationDialog
          open={deleting}
          onOpenChange={(open) => !open && setDeleting(false)}
          onConfirm={() => deleteMutation.mutate()}
          entityName={program.name}
          entityType="program"
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

// ============================================================================
// Timetable view
// ============================================================================

type TimetableViewProps = {
  programId: string;
  stages: Place[];
  happenings: Happening[];
  programStart: Date;
  programEnd: Date;
  returnTo: string;
};

function TimetableView({ programId, stages, happenings, programStart, programEnd, returnTo }: TimetableViewProps) {
  const navigate = useNavigate();

  // Build the list of days the program covers.
  const days = useMemo(() => {
    const result: Date[] = [];
    const cursor = new Date(programStart);
    cursor.setHours(0, 0, 0, 0);
    const last = new Date(programEnd);
    last.setHours(0, 0, 0, 0);
    while (cursor.getTime() <= last.getTime()) {
      result.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }, [programStart, programEnd]);

  // Initial values come from localStorage so the user lands back where they were
  // when they return to this program (e.g. after creating a performance).
  const [activeDayIdx, setActiveDayIdx] = useState<number>(() => {
    const stored = safeRead(lsKey.activeDay(programId));
    const parsed = stored != null ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>(() => {
    return safeRead(lsKey.orientation()) === 'vertical' ? 'vertical' : 'horizontal';
  });
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>(() => {
    return safeRead(lsKey.timeFormat()) === '12h' ? '12h' : '24h';
  });

  const formatTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h' });

  // Clamp activeDayIdx if the program's date range changed since last visit.
  useEffect(() => {
    if (activeDayIdx >= days.length) setActiveDayIdx(0);
  }, [days.length, activeDayIdx]);

  // Persist preferences whenever they change.
  useEffect(() => {
    safeWrite(lsKey.orientation(), orientation);
  }, [orientation]);
  useEffect(() => {
    safeWrite(lsKey.timeFormat(), timeFormat);
  }, [timeFormat]);
  useEffect(() => {
    safeWrite(lsKey.activeDay(programId), String(activeDayIdx));
  }, [programId, activeDayIdx]);

  // Scroll position persistence: each orientation has its own scroll container.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Restore scroll position whenever the orientation changes (which mounts a
  // different DOM subtree). useLayoutEffect to avoid a visible jump.
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const stored = safeRead(lsKey.scroll(programId, orientation));
    if (!stored) return;
    try {
      const { x, y } = JSON.parse(stored) as { x: number; y: number };
      el.scrollLeft = x;
      el.scrollTop = y;
    } catch {
      // ignore malformed state
    }
  }, [programId, orientation]);

  const persistScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    safeWrite(
      lsKey.scroll(programId, orientation),
      JSON.stringify({ x: el.scrollLeft, y: el.scrollTop })
    );
  };

  // Continuous timeline across the whole program: all days are concatenated into
  // one scrollable axis so a drag can cross midnight. Day pills scroll to a day
  // rather than switching the view.
  const daySlots = (24 * 60) / SLOT_MINUTES;
  const dayCount = Math.max(1, days.length);
  const totalSlots = dayCount * daySlots;
  const totalMinutes = dayCount * 24 * 60;
  const timelineStart = useMemo(() => {
    const d = days[0] ? new Date(days[0]) : new Date(programStart);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [days, programStart]);

  // Build the slot list for the whole timeline (00:00 of day 1 → 24:00 of the
  // last day) in 30-min steps.
  const slots = useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < totalSlots; i++) {
      result.push(new Date(timelineStart + i * SLOT_MINUTES * 60 * 1000));
    }
    return result;
  }, [timelineStart, totalSlots]);

  // All placed happenings (the continuous timeline shows every day at once).
  const happeningsForDay = useMemo(
    () => happenings.filter((h) => Boolean(h.start_time)),
    [happenings],
  );

  // Group happenings by place_id so we can render per stage row.
  const happeningsByStage = useMemo(() => {
    const map = new Map<string, Happening[]>();
    for (const h of happeningsForDay) {
      if (!h.place_id) continue;
      const list = map.get(h.place_id) ?? [];
      list.push(h);
      map.set(h.place_id, list);
    }
    return map;
  }, [happeningsForDay]);

  // Click-and-drag across empty slots to set start + end of a new happening.
  const [dragSel, setDragSel] = useState<{ stageId: string; a: number; b: number } | null>(null);
  const dragRef = useRef(dragSel);
  dragRef.current = dragSel;

  useEffect(() => {
    const onUp = () => {
      const sel = dragRef.current;
      if (!sel) return;
      setDragSel(null);
      const lo = Math.min(sel.a, sel.b);
      const hi = Math.max(sel.a, sel.b);
      const start = slots[lo];
      if (!start) return;
      persistScroll();
      const params = new URLSearchParams({
        createInProgram: programId,
        place: sel.stageId,
        start: start.toISOString(),
        returnTo,
      });
      // Multi-slot drag → also pass an end (slot end = its start + one slot).
      if (hi > lo && slots[hi]) {
        params.set('end', new Date(slots[hi].getTime() + SLOT_MINUTES * 60000).toISOString());
      }
      navigate(`/happenings?${params.toString()}`);
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, programId, returnTo]);

  const inDragSelection = (stageId: string, idx: number) =>
    !!dragSel && dragSel.stageId === stageId && idx >= Math.min(dragSel.a, dragSel.b) && idx <= Math.max(dragSel.a, dragSel.b);

  const handlePerformanceClick = (e: React.MouseEvent, h: Happening) => {
    e.stopPropagation();
    persistScroll();
    const params = new URLSearchParams({
      edit: h.id,
      returnTo,
    });
    navigate(`/happenings?${params.toString()}`);
  };

  // Minutes from the start of the continuous timeline (00:00 of the first day).
  const minutesIntoTimeline = (date: Date) => Math.max(0, (date.getTime() - timelineStart) / 60000);

  // Pixel size of one 30-min slot along the time axis.
  // (Width when horizontal; height when vertical.)
  const SLOT_SIZE_PX = 60;
  // Size of the cross-axis lane per stage.
  const LANE_SIZE_PX = orientation === 'horizontal' ? 56 : 180;
  const TIME_AXIS_LABEL_PX = 200; // width/height reserved for the time axis label column/row
  // Extent of one day along the time axis — used to scroll the timeline to a day.
  const dayExtentPx = daySlots * SLOT_SIZE_PX;

  // Day pills jump the timeline to a day's offset (along the active axis).
  const scrollToDay = (idx: number) => {
    setActiveDayIdx(idx);
    const el = scrollContainerRef.current;
    if (!el) return;
    if (orientation === 'horizontal') el.scrollTo({ left: idx * dayExtentPx, behavior: 'smooth' });
    else el.scrollTo({ top: idx * dayExtentPx, behavior: 'smooth' });
  };

  // Keep scroll position persisted and the active pill in sync with the scroll.
  const handleGridScroll = () => {
    persistScroll();
    const el = scrollContainerRef.current;
    if (!el) return;
    const pos = orientation === 'horizontal' ? el.scrollLeft : el.scrollTop;
    const idx = Math.max(0, Math.min(dayCount - 1, Math.round(pos / dayExtentPx)));
    setActiveDayIdx((cur) => (cur === idx ? cur : idx));
  };

  // Color a block by its happening type so tech/internal events read as different
  // from real performances at a glance.
  const blockClassByType = (type: Happening['type']): string => {
    switch (type) {
      case 'tech':
        return 'border-amber-500/50 bg-amber-500/15 hover:bg-amber-500/25';
      case 'show':
      case 'recording':
      case 'mix':
        return 'border-sky-500/50 bg-sky-500/15 hover:bg-sky-500/25';
      case 'performance':
      default:
        return 'border-primary/40 bg-primary/15 hover:bg-primary/25';
    }
  };

  const renderHappeningBlock = (h: Happening) => {
    if (!h.start_time) return null;
    const start = new Date(h.start_time);
    const end = h.end_time ? new Date(h.end_time) : new Date(start.getTime() + 60 * 60 * 1000);
    const startMin = minutesIntoTimeline(start);
    const endMin = Math.min(totalMinutes, minutesIntoTimeline(end));
    const offset = (startMin / SLOT_MINUTES) * SLOT_SIZE_PX;
    const length = Math.max(SLOT_SIZE_PX / 2, ((endMin - startMin) / SLOT_MINUTES) * SLOT_SIZE_PX);
    const positionStyle =
      orientation === 'horizontal'
        ? { left: offset, width: length, top: 4, bottom: 4 }
        : { top: offset, height: length, left: 4, right: 4 };
    return (
      <button
        type="button"
        key={h.id}
        onClick={(e) => handlePerformanceClick(e, h)}
        className={`absolute overflow-hidden rounded-md border px-2 text-left text-xs ${blockClassByType(h.type)}`}
        style={positionStyle}
        title={h.title}
      >
        <div className="truncate font-medium">{h.title}</div>
        <div className="truncate text-[10px] text-muted-foreground">
          {formatTime(start)}
          {h.end_time && (
            <>
              {' – '}
              {formatTime(end)}
            </>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {days.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {days.map((d, idx) => (
              <Button
                key={idx}
                size="sm"
                variant={idx === activeDayIdx ? 'default' : 'outline'}
                onClick={() => scrollToDay(idx)}
              >
                {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
              </Button>
            ))}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex gap-1 rounded-md border p-1">
            <Button
              size="sm"
              variant={timeFormat === '24h' ? 'default' : 'ghost'}
              onClick={() => setTimeFormat('24h')}
            >
              24h
            </Button>
            <Button
              size="sm"
              variant={timeFormat === '12h' ? 'default' : 'ghost'}
              onClick={() => setTimeFormat('12h')}
            >
              AM/PM
            </Button>
          </div>
          <div className="flex gap-1 rounded-md border p-1">
            <Button
              size="sm"
              variant={orientation === 'horizontal' ? 'default' : 'ghost'}
              onClick={() => setOrientation('horizontal')}
            >
              Horizontal
            </Button>
            <Button
              size="sm"
              variant={orientation === 'vertical' ? 'default' : 'ghost'}
              onClick={() => setOrientation('vertical')}
            >
              Vertical
            </Button>
          </div>
        </div>
      </div>

      {orientation === 'horizontal' ? (
        <div
          ref={scrollContainerRef}
          onScroll={handleGridScroll}
          className="overflow-auto rounded-md border"
          style={{ maxHeight: '70vh' }}
        >
          <div style={{ minWidth: TIME_AXIS_LABEL_PX + slots.length * SLOT_SIZE_PX }}>
            {/* Header: a day band over a row of half-hour labels (sticky to top) */}
            <div className="sticky top-0 z-20 border-b bg-muted/95 text-xs text-muted-foreground backdrop-blur">
              {/* Day band — marks where each day starts on the continuous axis */}
              <div className="flex border-b border-border/40">
                <div
                  className="sticky left-0 z-10 shrink-0 border-r bg-muted/95 backdrop-blur"
                  style={{ width: TIME_AXIS_LABEL_PX }}
                />
                <div className="flex">
                  {days.map((d, idx) => (
                    <div
                      key={idx}
                      style={{ width: dayExtentPx }}
                      className="shrink-0 border-l-2 border-foreground/30 px-2 py-1 font-medium first:border-l-0"
                    >
                      {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                  ))}
                </div>
              </div>
              {/* Half-hour labels */}
              <div className="flex">
                <div
                  className="sticky left-0 z-10 shrink-0 border-r bg-muted/95 p-2 font-medium backdrop-blur"
                  style={{ width: TIME_AXIS_LABEL_PX }}
                >
                  Stage
                </div>
                <div className="flex">
                  {slots.map((slot, idx) => (
                    <div
                      key={idx}
                      style={{ width: SLOT_SIZE_PX }}
                      className={
                        'shrink-0 border-r p-1 text-center ' +
                        (slot.getHours() === 0 && slot.getMinutes() === 0 ? 'border-l-2 border-foreground/30' : '')
                      }
                    >
                      {slot.getMinutes() === 0 ? formatTime(slot) : ''}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stage rows */}
            {stages.map((stage) => {
              const stageHappenings = happeningsByStage.get(stage.id) ?? [];
              return (
                <div key={stage.id} className="flex border-b last:border-b-0">
                  <div
                    className="sticky left-0 z-10 flex shrink-0 items-center border-r bg-background p-2 text-sm font-medium"
                    style={{ width: TIME_AXIS_LABEL_PX, height: LANE_SIZE_PX }}
                  >
                    {stage.name}
                  </div>
                  <div className="relative flex" style={{ height: LANE_SIZE_PX }}>
                    {slots.map((slot, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setDragSel({ stageId: stage.id, a: idx, b: idx }); }}
                        onMouseEnter={() => setDragSel((s) => (s && s.stageId === stage.id ? { ...s, b: idx } : s))}
                        style={{ width: SLOT_SIZE_PX, height: LANE_SIZE_PX }}
                        className={`shrink-0 border-r border-border/40 ${slot.getHours() === 0 && slot.getMinutes() === 0 ? 'border-l-2 border-foreground/20' : ''} ${inDragSelection(stage.id, idx) ? 'bg-primary/30' : 'hover:bg-muted/40'}`}
                        aria-label={`Schedule at ${slot.toLocaleTimeString()}`}
                      />
                    ))}
                    {stageHappenings.map(renderHappeningBlock)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          onScroll={handleGridScroll}
          className="overflow-auto rounded-md border"
          style={{ maxHeight: '70vh' }}
        >
          <div className="flex">
            {/* Time axis column on the left (sticky horizontally) */}
            <div className="sticky left-0 z-10 shrink-0 bg-background">
              <div
                className="sticky top-0 z-20 border-b border-r bg-muted/95 p-2 text-xs font-medium text-muted-foreground backdrop-blur"
                style={{ width: 80, height: 40 }}
              >
                Time
              </div>
              <div className="relative" style={{ width: 80, height: slots.length * SLOT_SIZE_PX }}>
                {slots.map((slot, idx) => {
                  const isMidnight = slot.getHours() === 0 && slot.getMinutes() === 0;
                  return (
                    <div
                      key={idx}
                      style={{
                        position: 'absolute',
                        top: idx * SLOT_SIZE_PX,
                        height: SLOT_SIZE_PX,
                        width: '100%',
                      }}
                      className={
                        'border-b border-r p-1 text-right text-xs text-muted-foreground ' +
                        (isMidnight ? 'border-t-2 border-foreground/30 font-medium text-foreground' : '')
                      }
                    >
                      {isMidnight
                        ? slot.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })
                        : slot.getMinutes() === 0
                          ? formatTime(slot)
                          : ''}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* One column per stage */}
            {stages.map((stage) => {
              const stageHappenings = happeningsByStage.get(stage.id) ?? [];
              return (
                <div key={stage.id} className="shrink-0 border-r last:border-r-0">
                  <div
                    className="sticky top-0 z-10 flex items-center justify-center border-b bg-muted/95 p-2 text-sm font-medium backdrop-blur"
                    style={{ width: LANE_SIZE_PX, height: 40 }}
                  >
                    {stage.name}
                  </div>
                  <div
                    className="relative"
                    style={{ width: LANE_SIZE_PX, height: slots.length * SLOT_SIZE_PX }}
                  >
                    {slots.map((slot, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setDragSel({ stageId: stage.id, a: idx, b: idx }); }}
                        onMouseEnter={() => setDragSel((s) => (s && s.stageId === stage.id ? { ...s, b: idx } : s))}
                        style={{
                          position: 'absolute',
                          top: idx * SLOT_SIZE_PX,
                          height: SLOT_SIZE_PX,
                          width: '100%',
                        }}
                        className={`border-b border-border/40 ${slot.getHours() === 0 && slot.getMinutes() === 0 ? 'border-t-2 border-foreground/20' : ''} ${inDragSelection(stage.id, idx) ? 'bg-primary/30' : 'hover:bg-muted/40'}`}
                        aria-label={`Schedule at ${slot.toLocaleTimeString()}`}
                      />
                    ))}
                    {stageHappenings.map(renderHappeningBlock)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Click an empty slot to add a performance. Click a performance to edit it.
      </p>
    </div>
  );
}

// ============================================================================
// List view (grouped by stage)
// ============================================================================

type ListViewProps = {
  programId: string;
  stages: Place[];
  happenings: Happening[];
  programStart: Date;
  returnTo: string;
};

function ListView({ programId, stages, happenings, returnTo }: ListViewProps) {
  const navigate = useNavigate();

  const happeningsByStage = useMemo(() => {
    const map = new Map<string, Happening[]>();
    for (const h of happenings) {
      if (!h.place_id) continue;
      const list = map.get(h.place_id) ?? [];
      list.push(h);
      map.set(h.place_id, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const aT = a.start_time ? new Date(a.start_time).getTime() : 0;
        const bT = b.start_time ? new Date(b.start_time).getTime() : 0;
        return aT - bT;
      });
    }
    return map;
  }, [happenings]);

  const goToCreate = (stageId: string) => {
    const params = new URLSearchParams({
      createInProgram: programId,
      place: stageId,
      returnTo,
    });
    navigate(`/happenings?${params.toString()}`);
  };

  const goToEdit = (h: Happening) => {
    const params = new URLSearchParams({ edit: h.id, returnTo });
    navigate(`/happenings?${params.toString()}`);
  };

  return (
    <div className="space-y-6">
      {stages.map((stage) => {
        const list = happeningsByStage.get(stage.id) ?? [];
        return (
          <div key={stage.id} className="rounded-md border">
            <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2">
              <div className="font-medium">{stage.name}</div>
              <Button size="sm" variant="outline" onClick={() => goToCreate(stage.id)}>
                <Plus className="mr-1 h-3 w-3" />
                Add performance
              </Button>
            </div>
            {list.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">No performances scheduled.</p>
            ) : (
              <ul className="divide-y">
                {list.map((h) => {
                  const start = h.start_time ? new Date(h.start_time) : null;
                  const end = h.end_time ? new Date(h.end_time) : null;
                  return (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => goToEdit(h)}
                        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-muted/40"
                      >
                        <div>
                          <div className="font-medium">{h.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {start?.toLocaleString()}
                            {end && ` – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">{h.status}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
